import React, { useEffect, useState } from 'react';
import { Button, DatePicker, Form, Input, InputNumber, Modal, Popconfirm, Segmented, Select, Switch } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { FctAviso, FctDato, FctEstado } from '../../fct/react';
import { SelectorRemoto, type OpcionRemota } from '../Oportunidades/SelectorRemoto';
import {
  borrarPropuesta, buscarClientes, crearPropuesta, editarPropuesta, errorAlta, leerAsesoresCartera, leerFarmacia, leerFarmacias,
  marcarVendida, notificarPropuesta, pasarACartera, type AsesorAlta, type CatalogosAlta, type DatosPropuestaAlta, type FarmaciaAlta, type PersonaAlta, type PropuestaAlta,
} from '../../api/transmisionesAltaApi';
import { estadoAlta, reglasEstado } from './estadosAlta';

export type AccionAlta = 'guardada' | 'vendida' | 'notificada';

interface FormularioPropuestaProps {
  catalogos: CatalogosAlta;
  /** La propuesta que se edita; sin ella, se crea una nueva. */
  propuesta?: PropuestaAlta;
  /** El asesor del token: quien recibe la farmacia en cartera si no elige otro. */
  asesorId?: number;
  /** Después de guardar, vender o notificar: la propuesta tal como queda y qué se hizo. */
  onGuardada: (propuesta: PropuestaAlta, accion: AccionAlta) => void;
  onBorrada: () => void;
  onCancelar: () => void;
}

interface ValoresFormulario {
  tipo: 0 | 1;
  provinciaId?: number;
  farmaciaId?: number;
  fecha?: Dayjs | null;
  enVentaDesde?: Dayjs | null;
  enVentaHasta?: Dayjs | null;
  enVentaExclusiva?: boolean;
  empresaIds?: number[];
  nuevosTitulares?: number[];
  tramoFactId?: number | null;
  horarioId?: number | null;
  regimenLocalId?: number | null;
  factor?: number | null;
  notas?: string;
}

// Los campos del formulario que la API puede señalar en un 400 (los nombres de DatosPropuestaAlta, en camelCase).
const CAMPOS: (keyof ValoresFormulario)[] = [
  'farmaciaId', 'fecha', 'enVentaDesde', 'enVentaHasta', 'enVentaExclusiva', 'empresaIds', 'nuevosTitulares',
  'tramoFactId', 'horarioId', 'regimenLocalId', 'factor', 'notas',
];

const DIA = 'YYYY-MM-DD';
const aDia = (d?: Dayjs | null) => (d ? d.format(DIA) : null);
const deDia = (d?: string | null) => (d ? dayjs(d) : null);
const opciones = (lista: { id: number; nombre?: string | null }[]) => lista.map((o) => ({ value: o.id, label: o.nombre ?? String(o.id) }));
const opcionCliente = (c: { id: number; nombre?: string | null; codigo?: string | null; poblacion?: string | null; provincia?: string | null }): OpcionRemota => ({
  value: c.id,
  label: c.nombre ?? `Cliente ${c.id}`,
  detalle: [c.codigo, c.poblacion && c.provincia ? `${c.poblacion} (${c.provincia})` : c.poblacion ?? c.provincia].filter(Boolean).join(' · ') || undefined,
});

/** Los valores del formulario a partir de una propuesta guardada. */
const valoresDe = (p: PropuestaAlta): ValoresFormulario => ({
  tipo: p.estadoId === 0 ? 0 : 1,
  farmaciaId: p.farmaciaId,
  fecha: deDia(p.fecha),
  enVentaDesde: deDia(p.enVentaDesde),
  enVentaHasta: deDia(p.enVentaHasta),
  enVentaExclusiva: p.enVentaExclusiva ?? false,
  empresaIds: p.empresaIds,
  nuevosTitulares: p.nuevosTitulares.map((t) => t.id),
  tramoFactId: p.tramoFactId ?? null,
  horarioId: p.horarioId ?? null,
  regimenLocalId: p.regimenLocalId ?? null,
  factor: p.factor ?? null,
  notas: p.notas ?? '',
});

/**
 * Lo que manda la API. El PUT sustituye la propuesta entera, así que van todos los campos, también los que el formulario no
 * pinta para su tipo: si no, la edición los borraría (QA en la PR #341).
 */
export const datosDe = (v: ValoresFormulario, farmaciaId: number): DatosPropuestaAlta => ({
  farmaciaId,
  fecha: aDia(v.fecha),
  enVentaDesde: aDia(v.enVentaDesde),
  enVentaHasta: aDia(v.enVentaHasta),
  enVentaExclusiva: v.enVentaExclusiva ?? null,
  empresaIds: v.empresaIds ?? [],
  nuevosTitulares: v.nuevosTitulares ?? [],
  tramoFactId: v.tramoFactId ?? null,
  horarioId: v.horarioId ?? null,
  regimenLocalId: v.regimenLocalId ?? null,
  factor: v.factor ?? null,
  notas: v.notas?.trim() ? v.notas.trim() : null,
});

/**
 * El formulario de una propuesta, de una columna para usarlo con una mano. Al crear se elige el tipo (farmacia en venta o
 * transmisión realizada), la provincia y la farmacia; los titulares actuales salen de Gescom. Las reglas de la API se
 * comprueban aquí antes de guardar, y un error de la API se muestra junto a su campo sin perder lo escrito.
 */
const FormularioPropuesta: React.FC<FormularioPropuestaProps> = ({ catalogos, propuesta, asesorId, onGuardada, onBorrada, onCancelar }) => {
  const [form] = Form.useForm<ValoresFormulario>();
  const tipo = Form.useWatch('tipo', form) ?? (propuesta ? (propuesta.estadoId === 0 ? 0 : 1) : 0);
  const provinciaId = Form.useWatch('provinciaId', form);
  const farmaciaId = Form.useWatch('farmaciaId', form);
  const exclusiva = Form.useWatch('enVentaExclusiva', form);
  const [farmacias, setFarmacias] = useState<FarmaciaAlta[]>([]);
  const [cargandoFarmacias, setCargandoFarmacias] = useState(false);
  const [titulares, setTitulares] = useState<PersonaAlta[]>(propuesta?.titulares ?? []);
  const [error, setError] = useState<{ mensaje: string; motivos?: string[] } | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [exito, setExito] = useState<string | null>(null);
  const [avisoEmail, setAvisoEmail] = useState<{ texto: string; enviado: boolean } | null>(null);
  const [cartera, setCartera] = useState<{ asesores: AsesorAlta[]; elegido?: number } | null>(null);
  const reglas = reglasEstado(propuesta?.estadoId ?? 0);
  const soloLectura = propuesta !== undefined && !reglas.editar;

  // Las farmacias de la provincia elegida, solo al crear.
  useEffect(() => {
    if (propuesta || provinciaId === undefined) return;
    let vigente = true;
    setCargandoFarmacias(true);
    leerFarmacias(provinciaId)
      .then((lista) => vigente && setFarmacias(lista))
      .catch((e) => vigente && setError({ mensaje: errorAlta(e).mensaje }))
      .finally(() => vigente && setCargandoFarmacias(false));
    return () => { vigente = false; };
  }, [propuesta, provinciaId]);

  // Al elegir una farmacia: sus titulares vigentes y, si el asesor no los ha puesto, su tramo, horario y régimen.
  useEffect(() => {
    if (propuesta || farmaciaId === undefined) return;
    let vigente = true;
    leerFarmacia(farmaciaId)
      .then((detalle) => {
        if (!vigente) return;
        setTitulares(detalle.titulares);
        const actual = form.getFieldsValue(['tramoFactId', 'horarioId', 'regimenLocalId']);
        form.setFieldsValue({
          tramoFactId: actual.tramoFactId ?? detalle.farmacia.tramoFactId ?? null,
          horarioId: actual.horarioId ?? detalle.farmacia.horarioId ?? null,
          regimenLocalId: actual.regimenLocalId ?? detalle.farmacia.regimenLocalId ?? null,
        });
      })
      .catch((e) => vigente && setError({ mensaje: errorAlta(e).mensaje }));
    return () => { vigente = false; };
  }, [propuesta, farmaciaId, form]);

  // Ejecuta una operación de la API: un 400 marca sus campos; lo demás sale en el aviso. Lo escrito no se pierde.
  const ejecutar = async (operacion: () => Promise<void>) => {
    setError(null);
    setOcupado(true);
    try {
      await operacion();
    } catch (e) {
      // El aviso lista también los motivos: alguno puede ser de un campo que no está en pantalla, como la farmacia al editar.
      const fallo = errorAlta(e);
      setError({ mensaje: fallo.mensaje, motivos: fallo.campos ? Object.values(fallo.campos) : undefined });
      if (fallo.campos) {
        form.setFields(Object.entries(fallo.campos)
          .filter(([campo]) => CAMPOS.includes(campo as keyof ValoresFormulario))
          .map(([name, motivo]) => ({ name: name as keyof ValoresFormulario, errors: [motivo] })));
      }
    } finally {
      setOcupado(false);
    }
  };

  // «Pasar a cartera» (#328): la ventana carga los asesores asignables y propone el del token. La propuesta no cambia.
  const abrirCartera = () => ejecutar(async () => {
    setExito(null);
    setAvisoEmail(null);
    const asesores = await leerAsesoresCartera();
    setCartera({ asesores, elegido: asesores.some((a) => a.id === asesorId) ? asesorId : undefined });
  });
  const confirmarCartera = () => ejecutar(async () => {
    const resultado = await pasarACartera(propuesta!.id, cartera?.elegido);
    setCartera(null);
    setExito(resultado.message);
    setAvisoEmail({ texto: resultado.avisoEmail, enviado: resultado.emailEncolado });
  });

  // onFinish solo trae los campos montados. Al editar se parte de todo lo que guarda el formulario (los valores de la
  // propuesta para los campos que no se pintan) y el tipo sale del estado de la propuesta. Al crear, solo lo montado: los
  // valores iniciales del otro tipo no son de esta propuesta (QA en la PR #341).
  const guardar = (montados: ValoresFormulario) => ejecutar(async () => {
    const v: ValoresFormulario = propuesta
      ? { ...form.getFieldsValue(true), ...montados, tipo: propuesta.estadoId === 0 ? 0 : 1 }
      : montados;
    const farmacia = propuesta?.farmaciaId ?? v.farmaciaId!;
    const datos = datosDe(v, farmacia);
    onGuardada(propuesta ? await editarPropuesta(propuesta.id, datos) : await crearPropuesta({ ...datos, estadoId: v.tipo }), 'guardada');
  });

  const farmaciaElegida = farmacias.find((f) => f.id === farmaciaId);
  const estado = propuesta ? estadoAlta(propuesta.estadoId) : null;

  return (
    <Form<ValoresFormulario>
      form={form}
      layout="vertical"
      size="large"
      className="gc-alta-formulario"
      disabled={ocupado || soloLectura}
      initialValues={propuesta ? valoresDe(propuesta) : { tipo: 0, enVentaDesde: dayjs(), enVentaExclusiva: false, empresaIds: [], nuevosTitulares: [] }}
      onFinish={guardar}
      onFinishFailed={() => setError({ mensaje: 'Revisa los datos marcados.' })}
    >
      {exito && <FctAviso tipo="exito" titulo={exito} />}
      {/* El aviso por email al asesor que la recibe (#329): si no salió, se dice por qué. */}
      {avisoEmail && <FctAviso tipo={avisoEmail.enviado ? 'info' : 'aviso'} titulo={avisoEmail.texto} />}
      {error && (
        <FctAviso tipo="error" titulo={error.mensaje}>
          {error.motivos && <ul>{error.motivos.map((m) => <li key={m}>{m}</li>)}</ul>}
        </FctAviso>
      )}

      {propuesta ? (
        <div className="gc-alta-datos">
          <FctDato etiqueta="Farmacia" valor={propuesta.farmacia ?? `Farmacia ${propuesta.farmaciaId}`} />
          {estado && <FctDato etiqueta="Estado" valor={<FctEstado personalizado={estado} />} />}
          {soloLectura && <FctAviso tipo="info" titulo="Esta propuesta ya la gestiona el equipo del listado: no se puede cambiar desde aquí." />}
        </div>
      ) : (
        <>
          <Form.Item name="tipo" label="Qué registras">
            <Segmented block options={[{ value: 0, label: 'Farmacia en venta' }, { value: 1, label: 'Transmisión realizada' }]} />
          </Form.Item>
          <Form.Item name="provinciaId" label="Provincia" rules={[{ required: true, message: 'Elige la provincia de la farmacia.' }]}>
            <Select
              showSearch
              optionFilterProp="label"
              placeholder="Provincia"
              options={opciones(catalogos.provincias)}
              onChange={() => { form.setFieldsValue({ farmaciaId: undefined }); setTitulares([]); }}
            />
          </Form.Item>
          <Form.Item
            name="farmaciaId"
            label="Farmacia"
            extra={farmaciaElegida?.localidad ?? undefined}
            rules={[
              { required: true, message: 'La farmacia es obligatoria.' },
              {
                validator: (_, id) => (farmacias.find((f) => f.id === id)?.transmisionPendiente
                  ? Promise.reject(new Error('Esta farmacia ya tiene una transmisión registrada.'))
                  : Promise.resolve()),
              },
            ]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              placeholder={provinciaId === undefined ? 'Elige antes la provincia' : 'Busca por dirección o localidad'}
              disabled={provinciaId === undefined}
              loading={cargandoFarmacias}
              options={farmacias.map((f) => ({
                value: f.id,
                label: f.transmisionPendiente ? `${f.nombre ?? f.id} · venta ya registrada` : f.nombre ?? String(f.id),
                disabled: f.transmisionPendiente,
              }))}
            />
          </Form.Item>
        </>
      )}

      <FctDato
        etiqueta="Titulares actuales"
        valor={titulares.length ? titulares.map((t) => t.nombre ?? `Cliente ${t.id}`).join(', ') : farmaciaId || propuesta ? 'La farmacia no tiene titulares vigentes en Gescom' : 'Elige la farmacia'}
      />

      {tipo === 0 ? (
        <>
          <Form.Item name="enVentaDesde" label="En venta desde" rules={[{ required: true, message: 'La fecha desde la que está en venta es obligatoria.' }]}>
            <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} inputReadOnly />
          </Form.Item>
          <Form.Item
            name="enVentaHasta"
            label="En venta hasta"
            dependencies={['enVentaDesde']}
            rules={[({ getFieldValue }) => ({
              validator: (_, hasta?: Dayjs | null) => (hasta && getFieldValue('enVentaDesde') && hasta.isBefore(getFieldValue('enVentaDesde'), 'day')
                ? Promise.reject(new Error('La fecha hasta no puede ser anterior a la fecha desde.'))
                : Promise.resolve()),
            })]}
          >
            <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} inputReadOnly />
          </Form.Item>
          <Form.Item name="enVentaExclusiva" label="En exclusiva" valuePropName="checked">
            <Switch />
          </Form.Item>
        </>
      ) : (
        <>
          <Form.Item name="fecha" label="Fecha de la transmisión" rules={[{ required: true, message: 'La fecha de la transmisión es obligatoria.' }]}>
            <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} inputReadOnly />
          </Form.Item>
          <Form.Item name="nuevosTitulares" label="Nuevos titulares">
            <SelectorRemoto
              multiple
              minimo={3}
              aria-label="Nuevos titulares"
              placeholder="Busca por nombre o código (3 letras como mínimo)"
              opcionesIniciales={propuesta?.nuevosTitulares.map(opcionCliente)}
              buscar={async (texto) => (await buscarClientes(texto)).clientes.map(opcionCliente)}
            />
          </Form.Item>
        </>
      )}

      <Form.Item
        name="empresaIds"
        label={tipo === 0 ? 'Empresas intermediarias' : 'Empresa'}
        dependencies={['enVentaExclusiva']}
        rules={[{
          validator: (_, ids?: number[]) => (exclusiva && tipo === 0 && (ids?.length ?? 0) > 1
            ? Promise.reject(new Error('Con exclusiva, una empresa como máximo.'))
            : Promise.resolve()),
        }]}
      >
        <Select mode="multiple" allowClear optionFilterProp="label" placeholder="Ninguna" options={opciones(catalogos.empresas)} />
      </Form.Item>

      <Form.Item name="factor" label="Factor" rules={[{ type: 'number', min: 0.1, max: 10, message: 'El factor va de 0,1 a 10.' }]}>
        <InputNumber min={0.1} max={10} step={0.1} precision={2} decimalSeparator="," style={{ width: '100%' }} inputMode="decimal" />
      </Form.Item>
      <Form.Item name="tramoFactId" label="Tramo de facturación">
        <Select allowClear placeholder="Sin tramo" options={opciones(catalogos.tramos)} />
      </Form.Item>
      <Form.Item name="horarioId" label="Horario">
        <Select allowClear placeholder="Sin horario" options={opciones(catalogos.horarios)} />
      </Form.Item>
      <Form.Item name="regimenLocalId" label="Régimen del local">
        <Select allowClear placeholder="Sin régimen" options={opciones(catalogos.regimenes)} />
      </Form.Item>
      <Form.Item name="notas" label="Notas">
        <Input.TextArea rows={4} maxLength={4000} showCount />
      </Form.Item>

      <div className="gc-alta-acciones">
        {!soloLectura && (
          <Button type="primary" htmlType="submit" block loading={ocupado}>
            {propuesta ? 'Guardar cambios' : 'Registrar propuesta'}
          </Button>
        )}
        {propuesta && reglas.vendida && (
          <Button block disabled={ocupado} onClick={() => ejecutar(async () => onGuardada(await marcarVendida(propuesta.id), 'vendida'))}>
            Vendida
          </Button>
        )}
        {propuesta && reglas.notificar && (
          <Button block disabled={ocupado} onClick={() => ejecutar(async () => onGuardada(await notificarPropuesta(propuesta.id), 'notificada'))}>
            Notificar
          </Button>
        )}
        {propuesta && reglas.editar && propuesta.puedePasarACartera && (
          <Button block disabled={ocupado} onClick={abrirCartera}>Pasar a cartera</Button>
        )}
        {propuesta && reglas.borrar && (
          <Popconfirm
            title="¿La farmacia ya no está en venta?"
            description="Se borra la propuesta."
            okText="Sí, borrarla"
            cancelText="No"
            onConfirm={() => ejecutar(async () => { await borrarPropuesta(propuesta.id); onBorrada(); })}
          >
            <Button block danger disabled={ocupado}>Ya no está en venta</Button>
          </Popconfirm>
        )}
        <Button block type="text" disabled={false} onClick={onCancelar}>Volver</Button>
      </div>

      <Modal
        open={cartera !== null}
        title="Pasar a cartera"
        okText="Pasar a cartera"
        cancelText="Cancelar"
        confirmLoading={ocupado}
        okButtonProps={{ disabled: cartera?.elegido === undefined }}
        onOk={confirmarCartera}
        onCancel={() => setCartera(null)}
      >
        <p>{propuesta?.farmacia ?? ''}</p>
        {error && cartera && <FctAviso tipo="error" titulo={error.mensaje} />}
        <Select
          showSearch
          style={{ width: '100%' }}
          size="large"
          optionFilterProp="label"
          aria-label="Asesor que la recibe"
          placeholder="Asesor que la recibe"
          value={cartera?.elegido}
          onChange={(id: number) => setCartera((actual) => (actual ? { ...actual, elegido: id } : actual))}
          options={(cartera?.asesores ?? []).map((a) => ({ value: a.id, label: a.nombre ?? String(a.id) }))}
        />
      </Modal>
    </Form>
  );
};

export default FormularioPropuesta;
